import type * as React from 'react';

import { cn } from 'src/lib/utils';

const Table = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & {
  ref?: React.Ref<HTMLTableElement>;
}) => (
  <div className='relative w-full overflow-auto rounded-lg'>
    <table
      className={cn(
        'w-full caption-bottom text-sm',
        '[&_thead_tr:first-child_th:first-child]:rounded-tl-lg',
        '[&_thead_tr:first-child_th:last-child]:rounded-tr-lg',
        '[&_tbody_tr:last-child_td:first-child]:rounded-bl-lg',
        '[&_tbody_tr:last-child_td:last-child]:rounded-br-lg',
        className,
      )}
      ref={ref}
      {...props}
    />
  </div>
);
Table.displayName = 'Table';

const TableHeader = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => (
  <thead
    className={cn('group/header [&_tr]:border-b', className)}
    ref={ref}
    {...props}
  />
);
TableHeader.displayName = 'TableHeader';

const TableBody = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => (
  <tbody
    className={cn('group/body [&_tr:last-child]:border-0', className)}
    ref={ref}
    {...props}
  />
);
TableBody.displayName = 'TableBody';

const TableFooter = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => (
  <tfoot
    className={cn(
      'border-t bg-slate-100/50 font-medium dark:bg-slate-800/50 [&>tr]:last:border-b-0',
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableFooter.displayName = 'TableFooter';

const TableRow = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & {
  ref?: React.Ref<HTMLTableRowElement>;
}) => (
  <tr
    className={cn(
      'group/row border-border-default border-b text-layout-foreground transition-colors data-[state=selected]:bg-slate-100 dark:data-[state=selected]:bg-slate-800',
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableRow.displayName = 'TableRow';

const TableHead = ({
  className,
  ref,
  ...props
}: (React.ThHTMLAttributes<HTMLTableCellElement> & {
  className?: string;
}) & { ref?: React.Ref<HTMLTableCellElement> }) => (
  <th
    className={cn(
      'px-4 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0',
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableHead.displayName = 'TableHead';

const TableCell = ({
  className,
  ref,
  ...props
}: (React.TdHTMLAttributes<HTMLTableCellElement> & {
  className?: string;
}) & { ref?: React.Ref<HTMLTableCellElement> }) => (
  <td
    className={cn(
      'px-4 py-2 align-middle [&:has([role=checkbox])]:pr-0',
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableCell.displayName = 'TableCell';

const TableCaption = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableCaptionElement> & {
  ref?: React.Ref<HTMLTableCaptionElement>;
}) => (
  <caption className={cn('mt-4 text-sm', className)} ref={ref} {...props} />
);
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
